import {useCallback, useEffect, useRef, type PointerEvent as ReactPointerEvent} from 'react';
import {Maximize2, Minimize2, Minus, X} from 'lucide-react';
import {APP_BY_ID} from '../data/apps';
import {MENU_BAR_HEIGHT, useDesktop} from './DesktopContext';
import {useWindowMotion} from './windowMotion';
import type {WindowState} from '../types';

type ResizeEdge = 'bottom' | 'corner' | 'right';

export function Window({state}: {state: WindowState}) {
  const {
    closeWindow,
    config,
    focusWindow,
    minimizeWindow,
    moveWindow,
    releaseSlot,
    resizeWindow,
    takeRestoreOrigin,
    toggleMaximize,
  } = useDesktop();

  const app = APP_BY_ID[state.appId];
  const elRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<number | null>(null);
  const pendingRef = useRef<(() => void) | null>(null);

  // Opening, parking, restoring, zooming and closing are all flights between
  // two boxes — see `windowMotion.ts`. `parked` (not `state.minimized`) is what
  // hides the window, because it has to stay on screen until it has arrived.
  const {parked, prewarm, requestClose, veiled} = useWindowMotion(elRef, state, {
    dockPosition: config.position,
    onClosed: () => closeWindow(state.id),
    releaseSlot,
    takeRestoreOrigin,
  });

  // Pointer moves are coalesced the same way the dock coalesces hover: at most
  // one geometry update per animation frame.
  const schedule = useCallback((task: () => void) => {
    pendingRef.current = task;
    if (frameRef.current !== null) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      const pending = pendingRef.current;
      pendingRef.current = null;
      pending?.();
    });
  }, []);

  useEffect(
    () => () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    },
    [],
  );

  const startDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || state.maximized) return;
    if ((event.target as HTMLElement).closest('[data-window-control]')) return;

    focusWindow(state.id);
    const startX = event.clientX;
    const startY = event.clientY;
    const originX = state.x;
    const originY = state.y;
    const pointerId = event.pointerId;
    document.body.classList.add('is-dragging-window');

    const onMove = (moveEvent: PointerEvent) => {
      if (moveEvent.pointerId !== pointerId) return;
      const nextX = originX + (moveEvent.clientX - startX);
      const nextY = originY + (moveEvent.clientY - startY);
      schedule(() =>
        moveWindow(
          state.id,
          Math.min(Math.max(nextX, 40 - state.width), window.innerWidth - 60),
          Math.min(Math.max(nextY, MENU_BAR_HEIGHT), window.innerHeight - 60),
        ),
      );
    };

    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      document.body.classList.remove('is-dragging-window');
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const startResize = (event: ReactPointerEvent<HTMLDivElement>, edge: ResizeEdge) => {
    if (event.button !== 0) return;
    event.stopPropagation();
    focusWindow(state.id);

    const startX = event.clientX;
    const startY = event.clientY;
    const originWidth = state.width;
    const originHeight = state.height;
    const minWidth = app?.window.minWidth ?? 320;
    const minHeight = app?.window.minHeight ?? 220;
    const pointerId = event.pointerId;

    const onMove = (moveEvent: PointerEvent) => {
      if (moveEvent.pointerId !== pointerId) return;
      const width =
        edge === 'bottom'
          ? originWidth
          : Math.max(minWidth, originWidth + (moveEvent.clientX - startX));
      const height =
        edge === 'right'
          ? originHeight
          : Math.max(minHeight, originHeight + (moveEvent.clientY - startY));
      schedule(() => resizeWindow(state.id, width, height));
    };

    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  if (!app) return null;
  const View = app.view;

  return (
    <div
      ref={elRef}
      data-window-id={state.id}
      className="window window-frame absolute left-0 top-0 flex flex-col overflow-hidden rounded-shell bg-canvas text-ink"
      style={{
        display: parked ? 'none' : undefined,
        height: `${state.height}px`,
        transform: `translate3d(${state.x}px, ${state.y}px, 0)`,
        // The genie overlay is holding a picture of this window; the element
        // itself must keep its layout box (so it stays measurable) but paint
        // nothing, or the two would be visible at once.
        visibility: veiled ? 'hidden' : undefined,
        width: `${state.width}px`,
        zIndex: state.z,
      }}
      onPointerDownCapture={() => focusWindow(state.id)}
    >
      <div
        className="bar flex h-10 shrink-0 cursor-default select-none items-center gap-3 px-4"
        onPointerDown={startDrag}
        onDoubleClick={() => toggleMaximize(state.id)}
      >
        <p className="pointer-events-none min-w-0 flex-1 truncate text-[12.5px] font-bold tracking-tight text-ink">
          {app.name}
        </p>
        {/* Labelled controls instead of coloured dots: they say what they do,
            and each 28px button carries a 40px hit area (::after) so an
            imprecise pointer never has to aim at the icon itself.

            Minimise also warms the genie: rasterising this window costs a fifth
            of a second, and a pointer arriving on that button is the warning
            that lets it happen before the click rather than after. */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            data-window-control="minimize"
            aria-label="Minimise"
            onClick={() => minimizeWindow(state.id)}
            onPointerEnter={prewarm}
            onFocus={prewarm}
            className="icon-btn relative flex h-7 w-7 items-center justify-center rounded-full after:absolute after:-inset-1.5 after:content-['']"
          >
            <Minus size={14} strokeWidth={2.5} />
          </button>
          <button
            type="button"
            data-window-control="zoom"
            aria-label="Zoom"
            onClick={() => toggleMaximize(state.id)}
            className="icon-btn relative flex h-7 w-7 items-center justify-center rounded-full after:absolute after:-inset-1.5 after:content-['']"
          >
            {state.maximized ? <Minimize2 size={13} strokeWidth={2.5} /> : <Maximize2 size={13} strokeWidth={2.5} />}
          </button>
          <button
            type="button"
            data-window-control="close"
            aria-label="Close"
            onClick={requestClose}
            className="icon-btn icon-btn--danger relative flex h-7 w-7 items-center justify-center rounded-full after:absolute after:-inset-1.5 after:content-['']"
          >
            <X size={15} strokeWidth={2.5} />
          </button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-hidden">
        <View />
      </div>

      {!state.maximized ? (
        <>
          <div
            data-genie-ignore=""
            onPointerDown={(event) => startResize(event, 'right')}
            className="absolute right-0 top-10 bottom-4 w-1.5 cursor-ew-resize"
          />
          <div
            data-genie-ignore=""
            onPointerDown={(event) => startResize(event, 'bottom')}
            className="absolute bottom-0 left-4 right-4 h-1.5 cursor-ns-resize"
          />
          <div
            data-genie-ignore=""
            onPointerDown={(event) => startResize(event, 'corner')}
            className="absolute bottom-0 right-0 h-4 w-4 cursor-nwse-resize"
          />
        </>
      ) : null}
    </div>
  );
}
